import { Chip, Tooltip } from '@mui/material';
import debounce from 'lodash.debounce';
import paths from '../constants/paths';

export const convertObjectToFormData = (obj) => {
  const formData = new FormData();

  Object.keys(obj).forEach((key) => {
    const value = obj[key];

    if (Array.isArray(value)) {
      value.forEach((item) => {
        formData.append(`${key}[]`, item);
      });
    } else {
      formData.append(key, value);
    }
  });

  return formData;
};

export const formatUrl = (url) => {
  if (!url) return '';
  url = url.trim();
  if (!/^https?:\/\//i.test(url)) {
    url = `https://${url}`;
  }
  return url;
};
export const validateImageDimensions = (file) => {
  return new Promise((resolve) => {
    const img = new Image();
    const objectURL = URL.createObjectURL(file);

    img.onload = () => {
      const { width, height } = img;
      URL.revokeObjectURL(objectURL);

      if (width < 300 || height < 300) {
        resolve(
          'Image dimensions are too small. Minimum size is 300x300 pixels.'
        );
      } else if (width > 2000 || height > 2000) {
        resolve(
          'Image dimensions are too large. Maximum size is 2000x2000 pixels.'
        );
      } else {
        resolve(true);
      }
    };

    img.onerror = () => {
      console.error('Error loading image.');
      URL.revokeObjectURL(objectURL);
      resolve('Invalid image file. Could not load.');
    };

    img.src = objectURL;
  });
};
export const fileSize = (size) => {
  const maxSizeInBytes = 10 * 1024 * 1024;
  if (size > maxSizeInBytes) {
    return true;
  }
  return false;
};

export const debouncedTrigger = debounce((field, trigger) => {
  trigger(field);
}, 100);

export const formatPhoneNumber = (value) => {
  let formatted = '';
  if (value?.length > 0) {
    if (value.length === 11 && value.startsWith('1')) {
      formatted = `+1 (${value.slice(1, 4)}) ${value.slice(4, 7)}-${value.slice(7, 11)}`;
    } else if (value.length <= 10) {
      formatted = `(${value.slice(0, 3)}) ${value.slice(3, 6)}-${value.slice(6, 10)}`;
    }
  }

  return formatted;
};

export const DegreesChip = ({ degrees }) => {
  if (!degrees || degrees.length === 0) return null;

  const degreeNames = degrees
    .map((d) => d.degree?.name?.split(" ")[0])
    .filter(Boolean);

  const label =
    degreeNames.length > 1
      ? `${degreeNames[0]} (+${degreeNames.length - 1})`
      : degreeNames[0];

  const tooltipTitle = degreeNames.join(", ");

  return (
    <Tooltip title={tooltipTitle} arrow>
      <Chip
        label={label}
        sx={{
          borderRadius: "6px",
          backgroundColor: "#0C8CE91A",
          color: "#0C8CE9",
          ml: 1,
          fontWeight: 600,
          fontSize: "14px",
          p: 0,
        }}
      />
    </Tooltip>
  );
};

export const isVideoFile = (url) => {
  if (!url) return false;
  const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov'];
  return videoExtensions.some(ext => url.toLowerCase().endsWith(ext));
};


export const getRouteName = (pathname) => {
  const cleanPath = pathname === '/' ? '/' : pathname.replace(/\/$/, '');
  
  if (cleanPath === '/') {
    return 'Home';
  }
  
  const exactMatch = Object.entries(paths).find(([key, value]) => {
    if (typeof value === 'string') {
      return value === cleanPath;
    }
    return false;
  });
  
  if (exactMatch) {
    const nameMap = {
      home: 'Home',
      onboarding: 'Registration',
      login: 'Login',
      forgetPassword: 'Forgot Password',
      resetPassword: 'Reset Password',
      changePassword: 'Change Password',
      unsubscribeEmail: 'Unsubscribe Email',
      invitationOnboarding: 'Accept Invite',
      notifications: 'Notifications',
      messages: 'Messages',
      groups: 'Discussion Groups',
      infiniteScroll: 'Post Summary',
      info: 'Info - General',
      groupInfo: 'Info - Groups',
      treatmentInfo: 'Info - Treatment'
    };
    
    return nameMap[exactMatch[0]] || formatRouteName(exactMatch[0]);
  }
  
  const dynamicMatch = Object.entries(paths).find(([key, value]) => {
    if (typeof value === 'function') {
      try {
        const pattern = value(':id', ':tid', ':account_type');
        const regexPattern = pattern
          .replace(/\/:id\//g, '\\/([^\\/]+)\\/')
          .replace(/\/:id$/g, '\\/([^\\/]+)')
          .replace(/:url/g, '([^\\/]+)')
          .replace(/\//g, '\\/');
        
        if (key === 'userProfile') {
          const basePattern = '\\/user-profile\\/([^\\/]+)\\/';
          const regex = new RegExp(`^${basePattern}(?:\\?.*)?$`);
          return regex.test(cleanPath);
        }
        
        const regex = new RegExp(`^${regexPattern}(?:\\?.*)?$`);
        return regex.test(cleanPath);
      } catch (e) {
        return false;
      }
    }
    return false;
  });
  
  if (dynamicMatch) {
    const dynamicNameMap = {
      userProfile: 'Profile',
      invitationOnboarding: 'Accept Invite'
    };
    
    return dynamicNameMap[dynamicMatch[0]] || formatRouteName(dynamicMatch[0]);
  }
  
  const nestedMatch = Object.entries(paths).find(([key, value]) => {
    if (typeof value === 'function' && key === 'network') {
      if (cleanPath.startsWith('/network/')) {
        return true;
      }
    }
    if (typeof value === 'function' && key === 'referrals') {
      if (cleanPath.startsWith('/referrals/')) {
        return true;
      }
    }
    if (typeof value === 'function' && key === 'post') {
      if (cleanPath.startsWith('/post/') && !cleanPath.includes('/summary')) {
        return true;
      }
    }
    return false;
  });
  
  if (nestedMatch) {
    if (cleanPath.includes('/network/all')) return 'Network - All';
    if (cleanPath.includes('/network/connections')) return 'Network - Connections';
    if (cleanPath.includes('/referrals/sent')) return 'Referrals - Sent';
    if (cleanPath.includes('/referrals/received')) return 'Referrals - Received';
    if (cleanPath.includes('/post/') && !cleanPath.includes('/summary')) return 'Single Post';
    
    return formatRouteName(nestedMatch[0]);
  }
  
  if (cleanPath === paths.infiniteScroll) {
    return 'Post Summary';
  }
  
  if (cleanPath.startsWith('/settings/')) {
    const settingsPage = cleanPath.replace('/settings/', '');
    const settingsMap = {
      'notifications': 'Settings - Notifications',
      'account': 'Settings - Account',
      'password': 'Settings - Password',
      'invitations': 'Settings - Invitations'
    };
    return settingsMap[settingsPage] || 'Settings';
  }
  
  if (cleanPath.startsWith('/profile/')) {
    const profileSection = cleanPath.replace('/profile/', '');
    const profileMap = {
      'basic': 'Edit Profile - Basic Info',
      'organization': 'Edit Profile - Organization',
      'degree': 'Edit Profile - Degree',
      'credentials': 'Edit Profile - Credentials',
      'training': 'Edit Profile - Training',
      'availability': 'Edit Profile - Availability',
      'demographics': 'Edit Profile - Demographics',
      'specialties': 'Edit Profile - Specialties',
      'treatment-modalities': 'Edit Profile - Treatment Modalities',
      'session-format': 'Edit Profile - Session Format',
      'client-focus': 'Edit Profile - Client Focus',
      'fees': 'Edit Profile - Fees',
      'team': 'Edit Profile - Team',
      'services': 'Edit Profile - Services',
      'images': 'Edit Profile - Images',
      'faqs': 'Edit Profile - FAQs'
    };
    return profileMap[profileSection] || 'Edit Profile';
  }
  
  return null;
};

const formatRouteName = (key) => {
  const withSpaces = key.replace(/([A-Z])/g, ' $1').trim();
  return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1);
};